/**
 * Server-side password policy.
 *
 * Client-side rules are advisory only -- every password write path must call
 * `validatePassword` before the value reaches Mongoose. It cannot be enforced
 * with a schema validator: the `pre('findOneAndUpdate')` hook in the User model
 * bcrypt-hashes the password during query execution, so an update validator
 * would be measuring a 60-character hash rather than what the user typed.
 *
 * Unlike `core/validationRules`, these helpers fail closed -- a missing or
 * empty password is a failure, not a pass.
 */

export const PASSWORD_MIN_LENGTH = 12;

// bcrypt silently truncates beyond 72 bytes, so there is nothing to gain above
// it. Capping lower than this only blocks passphrases.
export const PASSWORD_MAX_LENGTH = 72;

// Shared by every password field so the wording cannot drift away from the
// rules actually enforced below.
export const PASSWORD_HELP_TEXT = `At least ${PASSWORD_MIN_LENGTH} characters, including an uppercase letter, a lowercase letter, a number and a symbol.`;

export const PASSWORD_ERROR_TEXT = `Password must be at least ${PASSWORD_MIN_LENGTH} characters and include an uppercase letter, a lowercase letter, a number and a symbol.`;

const RULES = [
  {
    key: 'minLength',
    test: value => value.length >= PASSWORD_MIN_LENGTH,
    message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
  },
  {
    key: 'maxLength',
    test: value => value.length <= PASSWORD_MAX_LENGTH,
    message: `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`,
  },
  {
    key: 'lowercase',
    test: value => /[a-z]/.test(value),
    message: 'Password must contain a lowercase letter.',
  },
  {
    key: 'uppercase',
    test: value => /[A-Z]/.test(value),
    message: 'Password must contain an uppercase letter.',
  },
  {
    key: 'number',
    test: value => /[0-9]/.test(value),
    message: 'Password must contain a number.',
  },
  {
    key: 'symbol',
    test: value => /[^A-Za-z0-9]/.test(value),
    message: 'Password must contain a symbol.',
  },
];

/**
 * Returns an array of human-readable failures. Empty array means the password
 * satisfies the policy.
 */
export function validatePassword(password) {
  if (typeof password !== 'string' || password.length === 0) {
    return ['Password is required.'];
  }

  return RULES.filter(rule => !rule.test(password)).map(rule => rule.message);
}

export function isValidPassword(password) {
  return validatePassword(password).length === 0;
}



// WEBPACK FOOTER //
// ./src/core/passwordPolicy.js